import React, { useState, useEffect, useCallback } from 'react';
import {
  FiAlertTriangle as AlertTriangle,
  FiCheckCircle as CheckCircle,
  FiActivity as Activity,
  FiRefreshCw as RefreshCw,
  FiTrendingUp as TrendingUp,
} from 'react-icons/fi';
import { getMemoryLeakDetection } from '../../../../../services/aiOperationsApi';

interface WindowOption {
  value: number;
  label: string;
}

const WINDOW_OPTIONS: WindowOption[] = [
  { value: 1, label: '1 hour' },
  { value: 6, label: '6 hours' },
  { value: 24, label: '24 hours' },
  { value: 72, label: '3 days' },
  { value: 168, label: '7 days' },
];

interface LeakStatus {
  status: 'healthy' | 'potential' | 'confirmed' | 'likely' | 'unknown';
  color: 'green' | 'yellow' | 'orange' | 'red' | 'gray';
  message: string;
}

interface DetectionResult {
  leak_score?: number | null;
  confidence?: string;
  growth_rate_bytes_per_hour?: number | null;
  trendline?: {
    r_squared?: number;
    slope?: number;
  };
  trend?: {
    slope_bytes_per_hour?: number;
    r_squared?: number;
  };
  data_points?: number;
}

const getLeakStatus = (score: number | null | undefined, confidence: string | undefined): LeakStatus => {
  if (score == null) return { status: 'unknown', color: 'gray', message: 'No data' };
  if (score < 0.3) return { status: 'healthy', color: 'green', message: 'No memory leak detected' };
  if (score < 0.6) return { status: 'potential', color: 'yellow', message: 'Potential memory leak' };
  if (confidence === 'high') return { status: 'confirmed', color: 'red', message: 'Memory leak detected' };
  return { status: 'likely', color: 'orange', message: 'Likely memory leak' };
};

const formatBytesPerHour = (bytesPerHour: number | null | undefined): string => {
  if (bytesPerHour == null) return '--';
  const abs = Math.abs(bytesPerHour);
  const sign = bytesPerHour >= 0 ? '+' : '-';
  if (abs >= 1073741824) return `${sign}${(abs / 1073741824).toFixed(2)} GB/h`;
  if (abs >= 1048576) return `${sign}${(abs / 1048576).toFixed(2)} MB/h`;
  if (abs >= 1024) return `${sign}${(abs / 1024).toFixed(2)} KB/h`;
  return `${sign}${abs.toFixed(0)} B/h`;
};

interface Props {
  agentName?: string;
  projectId?: string | null;
}

const MemoryLeakDetectionCard: React.FC<Props> = ({ agentName, projectId }) => {
  const [selectedAgent, setSelectedAgent] = useState(agentName ?? '');
  const [windowHours, setWindowHours] = useState(24);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detection, setDetection] = useState<DetectionResult | null>(null);

  useEffect(() => {
    setSelectedAgent(agentName ?? '');
  }, [agentName]);

  const fetchDetection = useCallback(async () => {
    if (!selectedAgent) {
      setDetection(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await getMemoryLeakDetection(selectedAgent, windowHours, projectId);
      setDetection(result as DetectionResult);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to analyze';
      console.error('Failed to fetch memory leak detection:', err);
      setError(message);
      setDetection(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedAgent, windowHours]);

  useEffect(() => {
    fetchDetection();
  }, [selectedAgent, windowHours, fetchDetection]);

  const leakInfo = getLeakStatus(detection?.leak_score, detection?.confidence);

  const statusIconMap: Record<LeakStatus['status'], React.ComponentType<{ className?: string }>> = {
    healthy: CheckCircle,
    potential: AlertTriangle,
    likely: AlertTriangle,
    confirmed: AlertTriangle,
    unknown: Activity,
  };
  const StatusIcon = statusIconMap[leakInfo.status];

  const statusColors: Record<LeakStatus['color'], string> = {
    green: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30',
    yellow: 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30',
    orange: 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30',
    red: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30',
    gray: 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700',
  };

  let statusDisplay: React.ReactNode;
  if (loading) {
    statusDisplay = (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  } else if (error) {
    statusDisplay = (
      <div className="text-center py-4 text-red-500 dark:text-red-400 text-sm">
        {error}
      </div>
    );
  } else if (selectedAgent) {
    statusDisplay = null;
  } else {
    statusDisplay = (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
        Select an agent in the page filters to analyze memory patterns
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Memory Leak Detection
        </h3>
        <button
          onClick={fetchDetection}
          disabled={loading || !selectedAgent}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          title="Refresh analysis"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 gap-3 mb-4">
        <div>
          <label htmlFor="time-window" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Time Window</label>
          <select id="time-window"
            value={windowHours}
            onChange={(e) => setWindowHours(Number.parseInt(e.target.value, 10))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded
              bg-white dark:bg-gray-700 text-gray-900 dark:text-white
              focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {WINDOW_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Status Display */}
      {statusDisplay}
      {!loading && !error && selectedAgent && detection ? (
        <div className="space-y-4">
          {/* Status Badge */}
          <div className={`flex items-center gap-3 p-3 rounded-lg ${statusColors[leakInfo.color]}`}>
            <StatusIcon className="w-5 h-5" />
            <div>
              <p className="font-medium">{leakInfo.message}</p>
              {detection.confidence && (
                <p className="text-xs opacity-80">Confidence: {detection.confidence}</p>
              )}
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Leak Score</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {detection.leak_score == null ? '--' : (detection.leak_score * 100).toFixed(1) + '%'}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Growth Rate</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {formatBytesPerHour(detection.growth_rate_bytes_per_hour ?? detection.trend?.slope_bytes_per_hour)}
              </p>
            </div>
          </div>

          {/* Trendline Info */}
          {detection.trendline && (
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <div className="flex justify-between">
                <span>Data Points:</span>
                <span>{detection.data_points ?? '--'}</span>
              </div>
              <div className="flex justify-between">
                <span>R-squared:</span>
                <span>{detection.trendline.r_squared?.toFixed(4) ?? '--'}</span>
              </div>
              <div className="flex justify-between">
                <span>Slope:</span>
                <span>{detection.trendline.slope?.toFixed(2) ?? '--'} bytes/s</span>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {leakInfo.status !== 'healthy' && leakInfo.status !== 'unknown' && (
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Recommendations:</p>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
                <li>Review recent code changes affecting memory usage</li>
                <li>Check for unclosed resources or event listeners</li>
                <li>Profile the application to identify leak sources</li>
                {leakInfo.status === 'confirmed' && (
                  <li className="text-red-600 dark:text-red-400">Consider restarting the agent soon</li>
                )}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
          No detection data available
        </div>
      )}
    </div>
  );
};

export default MemoryLeakDetectionCard;
